import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import FileUpload from '../../components/FileUpload';
import { Save, Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminContent() {
  const [homepage, setHomepage] = useState(null);
  const [contact, setContact] = useState(null);
  const [story, setStory] = useState(null);
  const [mission, setMission] = useState(null);
  const [footer, setFooter] = useState(null);
  const [heroCfg, setHeroCfg] = useState(null);
  const [trust, setTrust] = useState([]);
  const [generalFaqs, setGeneralFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCatSlug, setActiveCatSlug] = useState('');
  const [basketFaqs, setBasketFaqs] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/admin/settings/homepage').then(r => setHomepage(r.data.value)),
      api.get('/admin/settings/contact').then(r => setContact(r.data.value)),
      api.get('/admin/settings/our_story').then(r => setStory(r.data.value)),
      api.get('/admin/settings/mission').then(r => setMission(r.data.value)),
      api.get('/admin/settings/footer').then(r => setFooter(r.data.value || {})).catch(() => setFooter({})),
      api.get('/admin/settings/homepage_hero').then(r => setHeroCfg(r.data.value || { images: [], auto_rotate: true, interval_ms: 4000 })).catch(() => setHeroCfg({ images: [], auto_rotate: true, interval_ms: 4000 })),
      api.get('/admin/settings/homepage_trust_tags').then(r => setTrust(r.data.value || [])).catch(() => setTrust([])),
      api.get('/admin/faq/general').then(r => setGeneralFaqs(r.data.value || [])).catch(() => setGeneralFaqs([])),
      api.get('/categories').then(r => {
        setCategories(r.data || []);
        if (r.data?.[0]) setActiveCatSlug(r.data[0].slug);
      }),
      api.get('/admin/policies').then(r => setPolicies(r.data || [])).catch(() => setPolicies([])),
    ]).catch(console.error);
  }, []);

  useEffect(() => {
    if (!activeCatSlug) return;
    api.get(`/admin/faq/${activeCatSlug}`).then(r => setBasketFaqs(r.data.value || [])).catch(() => setBasketFaqs([]));
  }, [activeCatSlug]);

  const save = async (key, value) => {
    setSaving(true);
    try {
      await api.put(`/admin/settings/${key}`, { value });
      toast.success(`${key} updated!`);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const saveFaq = async (section, items) => {
    setSaving(true);
    try {
      await api.put(`/admin/faq/${section}`, { value: items });
      toast.success('FAQ saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const moveItem = (arr, setter, i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    const copy = [...arr];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setter(copy);
  };

  const refreshPolicies = () => api.get('/admin/policies').then(r => setPolicies(r.data || []));

  if (!homepage) return <div className="flex items-center justify-center h-full p-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="admin-content-page" className="p-6 lg:p-8 max-w-5xl">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-serif font-medium">Content Management</h1>
        <p className="text-sm text-muted-foreground mt-1">Edit pages, homepage, FAQs, policies, footer. Changes reflect live.</p>
      </div>

      <Tabs defaultValue="homepage">
        <TabsList className="rounded-full mb-6 flex-wrap h-auto">
          <TabsTrigger value="homepage" className="rounded-full">Homepage</TabsTrigger>
          <TabsTrigger value="hero" className="rounded-full">Hero Images</TabsTrigger>
          <TabsTrigger value="trust" className="rounded-full">Trust Tags</TabsTrigger>
          <TabsTrigger value="faqs" className="rounded-full">FAQ Manager</TabsTrigger>
          <TabsTrigger value="policies" className="rounded-full">Policies</TabsTrigger>
          <TabsTrigger value="footer" className="rounded-full">Footer</TabsTrigger>
          <TabsTrigger value="contact" className="rounded-full">Contact</TabsTrigger>
          <TabsTrigger value="story" className="rounded-full">Our Story</TabsTrigger>
          <TabsTrigger value="mission" className="rounded-full">Mission</TabsTrigger>
        </TabsList>

        <TabsContent value="homepage">
          <Card className="rounded-xl">
            <CardHeader><CardTitle className="text-lg">Homepage Hero</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Headline</Label><Input value={homepage.hero_headline || ''} onChange={e => setHomepage({...homepage, hero_headline: e.target.value})} className="mt-1" /></div>
              <div><Label>Subheadline</Label><Textarea value={homepage.hero_subheadline || ''} onChange={e => setHomepage({...homepage, hero_subheadline: e.target.value})} className="mt-1" rows={2} /></div>
              <div><Label>CTA Button Text</Label><Input value={homepage.hero_cta || ''} onChange={e => setHomepage({...homepage, hero_cta: e.target.value})} className="mt-1" /></div>
              <div><Label>Hero Background Image (fallback, use Hero Images tab for rotation)</Label>
                <FileUpload folder="homepage" onUpload={(url) => setHomepage({...homepage, hero_image: url})} existingFiles={homepage.hero_image ? [homepage.hero_image] : []} />
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={homepage.seasonal_banner_enabled || false} onChange={e => setHomepage({...homepage, seasonal_banner_enabled: e.target.checked})} />
                <Label>Enable Seasonal Marquee Banner</Label>
              </div>
              {homepage.seasonal_banner_enabled && (
                <div><Label>Seasonal Banner Text</Label><Input value={homepage.seasonal_banner_text || ''} onChange={e => setHomepage({...homepage, seasonal_banner_text: e.target.value})} className="mt-1" /></div>
              )}
              <Button data-testid="save-homepage" onClick={() => save('homepage', homepage)} className="rounded-full" disabled={saving}>
                <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Homepage'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hero">
          <Card className="rounded-xl">
            <CardHeader><CardTitle className="text-lg">Rotating Hero Images</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <FileUpload folder="homepage/hero" multiple existingFiles={heroCfg?.images || []} onUpload={(urls) => setHeroCfg({...heroCfg, images: urls})} label="Upload hero images (auto-rotated on homepage)" />
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={heroCfg?.auto_rotate ?? true} onChange={e => setHeroCfg({...heroCfg, auto_rotate: e.target.checked})} />
                <Label>Auto-rotate images</Label>
              </div>
              <div>
                <Label>Rotation Interval (ms)</Label>
                <Input type="number" value={heroCfg?.interval_ms || 4000} onChange={e => setHeroCfg({...heroCfg, interval_ms: parseInt(e.target.value) || 4000})} className="w-32 mt-1" />
              </div>
              <Button data-testid="save-hero-images" onClick={() => save('homepage_hero', heroCfg)} disabled={saving} className="rounded-full"><Save className="h-4 w-4 mr-2" /> Save Hero</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trust">
          <Card className="rounded-xl">
            <CardHeader><CardTitle className="text-lg">Trust Tags Strip</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">Icons: leaf, sun, truck, shield-check, sparkles</p>
              {trust.map((t, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center">
                  <Input placeholder="Label" value={t.label} onChange={e => { const a = [...trust]; a[i] = {...a[i], label: e.target.value}; setTrust(a); }} />
                  <Input placeholder="Icon name" value={t.icon} onChange={e => { const a = [...trust]; a[i] = {...a[i], icon: e.target.value}; setTrust(a); }} />
                  <Button variant="ghost" size="icon" onClick={() => moveItem(trust, setTrust, i, -1)}><ChevronUp className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setTrust(trust.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              ))}
              <Button variant="ghost" size="sm" onClick={() => setTrust([...trust, { label: '', icon: 'leaf' }])}><Plus className="h-3 w-3 mr-1" /> Add Tag</Button>
              <Button data-testid="save-trust-tags" onClick={() => save('homepage_trust_tags', trust)} disabled={saving} className="rounded-full w-full"><Save className="h-4 w-4 mr-2" /> Save Trust Tags</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="faqs">
          <div className="space-y-4">
            <Card className="rounded-xl">
              <CardHeader><CardTitle className="text-lg">General FAQs (Homepage)</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {generalFaqs.map((f, i) => (
                  <div key={i} className="grid grid-cols-[1fr_2fr_auto_auto_auto] gap-2 items-center">
                    <Input placeholder="Question" value={f.question || f.q || ''} onChange={e => { const a = [...generalFaqs]; a[i] = {...a[i], question: e.target.value}; setGeneralFaqs(a); }} />
                    <Textarea rows={1} placeholder="Answer" value={f.answer || f.a || ''} onChange={e => { const a = [...generalFaqs]; a[i] = {...a[i], answer: e.target.value}; setGeneralFaqs(a); }} />
                    <Button variant="ghost" size="icon" onClick={() => moveItem(generalFaqs, setGeneralFaqs, i, -1)}><ChevronUp className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => moveItem(generalFaqs, setGeneralFaqs, i, 1)}><ChevronDown className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setGeneralFaqs(generalFaqs.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setGeneralFaqs([...generalFaqs, { question: '', answer: '' }])}><Plus className="h-3 w-3 mr-1" /> Add FAQ</Button>
                  <Button data-testid="save-general-faq" size="sm" onClick={() => saveFaq('general', generalFaqs)} disabled={saving} className="rounded-full ml-auto"><Save className="h-3.5 w-3.5 mr-1" /> Save General FAQs</Button>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-3">
                  Basket Questions (per Category)
                  <select value={activeCatSlug} onChange={e => setActiveCatSlug(e.target.value)}
                    data-testid="basket-faq-category-select"
                    className="h-9 px-3 rounded-lg border border-input text-sm bg-background">
                    {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {basketFaqs.map((f, i) => (
                  <div key={i} className="grid grid-cols-[1fr_2fr_auto_auto_auto] gap-2 items-center">
                    <Input placeholder="Question" value={f.question || f.q || ''} onChange={e => { const a = [...basketFaqs]; a[i] = {...a[i], question: e.target.value}; setBasketFaqs(a); }} />
                    <Textarea rows={1} placeholder="Answer" value={f.answer || f.a || ''} onChange={e => { const a = [...basketFaqs]; a[i] = {...a[i], answer: e.target.value}; setBasketFaqs(a); }} />
                    <Button variant="ghost" size="icon" onClick={() => moveItem(basketFaqs, setBasketFaqs, i, -1)}><ChevronUp className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => moveItem(basketFaqs, setBasketFaqs, i, 1)}><ChevronDown className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setBasketFaqs(basketFaqs.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setBasketFaqs([...basketFaqs, { question: '', answer: '' }])}><Plus className="h-3 w-3 mr-1" /> Add FAQ</Button>
                  <Button data-testid="save-basket-faq" size="sm" onClick={() => saveFaq(`basket_${activeCatSlug}`, basketFaqs)} disabled={saving} className="rounded-full ml-auto"><Save className="h-3.5 w-3.5 mr-1" /> Save Basket FAQs for {categories.find(c => c.slug === activeCatSlug)?.name}</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="policies">
          <PolicyManager policies={policies} refresh={refreshPolicies} />
        </TabsContent>

        <TabsContent value="footer">
          <Card className="rounded-xl">
            <CardHeader><CardTitle className="text-lg">Footer Editor</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Brand Description</Label><Textarea value={footer?.brand_description || ''} onChange={e => setFooter({...footer, brand_description: e.target.value})} rows={3} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Address</Label><Input value={footer?.address || ''} onChange={e => setFooter({...footer, address: e.target.value})} /></div>
                <div><Label>Email</Label><Input value={footer?.email || ''} onChange={e => setFooter({...footer, email: e.target.value})} /></div>
                <div><Label>Phone</Label><Input value={footer?.phone || ''} onChange={e => setFooter({...footer, phone: e.target.value})} /></div>
                <div><Label>Copyright</Label><Input value={footer?.copyright || ''} onChange={e => setFooter({...footer, copyright: e.target.value})} /></div>
              </div>
              <Button data-testid="save-footer" onClick={() => save('footer', footer)} disabled={saving} className="rounded-full"><Save className="h-4 w-4 mr-2" /> Save Footer</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contact">
          {contact && (
            <Card className="rounded-xl">
              <CardHeader><CardTitle className="text-lg">Contact Page</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label>Page Heading</Label><Input value={contact.heading || ''} onChange={e => setContact({...contact, heading: e.target.value})} className="mt-1" /></div>
                <div><Label>Description</Label><Textarea value={contact.description || ''} onChange={e => setContact({...contact, description: e.target.value})} className="mt-1" rows={2} /></div>
                <div><Label>WhatsApp Number</Label><Input value={contact.whatsapp || ''} onChange={e => setContact({...contact, whatsapp: e.target.value})} className="mt-1" placeholder="+919876543210" /></div>
                <div><Label>Phone Number</Label><Input value={contact.phone || ''} onChange={e => setContact({...contact, phone: e.target.value})} className="mt-1" /></div>
                <div><Label>Company Email</Label><Input value={contact.email || ''} onChange={e => setContact({...contact, email: e.target.value})} className="mt-1" /></div>
                <Button onClick={() => save('contact', contact)} className="rounded-full" disabled={saving}>
                  <Save className="h-4 w-4 mr-2" /> Save Contact
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="story">
          {story && (
            <Card className="rounded-xl">
              <CardHeader><CardTitle className="text-lg">Our Story Page</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label>Heading</Label><Input value={story.heading || ''} onChange={e => setStory({...story, heading: e.target.value})} className="mt-1" /></div>
                <div>
                  <Label>Hero Image</Label>
                  <FileUpload folder="pages" onUpload={(url) => setStory({...story, hero_image: url})} existingFiles={story.hero_image ? [story.hero_image] : []} />
                </div>
                <div><Label>Story Content</Label><Textarea value={story.content || ''} onChange={e => setStory({...story, content: e.target.value})} className="mt-1" rows={8} /></div>
                <div><Label>Mission Heading</Label><Input value={story.mission_heading || ''} onChange={e => setStory({...story, mission_heading: e.target.value})} className="mt-1" /></div>
                <div><Label>Mission Text</Label><Textarea value={story.mission_text || ''} onChange={e => setStory({...story, mission_text: e.target.value})} className="mt-1" rows={4} /></div>
                <Button onClick={() => save('our_story', story)} className="rounded-full" disabled={saving}>
                  <Save className="h-4 w-4 mr-2" /> Save Story
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="mission">
          {mission && (
            <Card className="rounded-xl">
              <CardHeader><CardTitle className="text-lg">Mission Page</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label>Heading</Label><Input value={mission.heading || ''} onChange={e => setMission({...mission, heading: e.target.value})} className="mt-1" /></div>
                <div>
                  <Label>Hero Image</Label>
                  <FileUpload folder="pages" onUpload={(url) => setMission({...mission, hero_image: url})} existingFiles={mission.hero_image ? [mission.hero_image] : []} />
                </div>
                <div><Label>Content</Label><Textarea value={mission.content || ''} onChange={e => setMission({...mission, content: e.target.value})} className="mt-1" rows={8} /></div>
                <Button onClick={() => save('mission', mission)} className="rounded-full" disabled={saving}>
                  <Save className="h-4 w-4 mr-2" /> Save Mission
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PolicyManager({ policies, refresh }) {
  const [newPolicy, setNewPolicy] = useState({ policy_name: '', policy_slug: '', pdf_url: '', is_published: true });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!newPolicy.policy_name || !newPolicy.pdf_url) { toast.error('Name + PDF required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/policies', newPolicy);
      toast.success('Policy added');
      setNewPolicy({ policy_name: '', policy_slug: '', pdf_url: '', is_published: true });
      refresh();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };
  const update = async (p, patch) => {
    await api.put(`/admin/policies/${p.id}`, patch);
    refresh();
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.policy_name}"?`)) return;
    await api.delete(`/admin/policies/${p.id}`);
    refresh();
  };

  return (
    <Card className="rounded-xl" data-testid="policies-panel">
      <CardHeader><CardTitle className="text-lg">Site Policies</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {policies.length === 0 && <p className="text-xs text-muted-foreground">No policies yet. Add your first one below.</p>}
        {policies.map((p) => (
          <div key={p.id} data-testid={`policy-row-${p.id}`} className="border rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Input value={p.policy_name} onChange={e => update(p, { policy_name: e.target.value })} className="max-w-xs" />
              <Badge variant={p.is_published ? 'default' : 'secondary'} className="rounded-full text-[10px]">{p.is_published ? 'Published' : 'Draft'}</Badge>
              <Button variant="outline" size="sm" className="rounded-full h-7 text-xs" onClick={() => update(p, { is_published: !p.is_published })}>
                {p.is_published ? 'Unpublish' : 'Publish'}
              </Button>
              <a href={p.pdf_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">View PDF</a>
              <Button variant="ghost" size="icon" onClick={() => remove(p)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
            <FileUpload folder="policies" accept=".pdf" existingFiles={p.pdf_url ? [p.pdf_url] : []}
              onUpload={url => update(p, { pdf_url: url })} label="Re-upload PDF" />
          </div>
        ))}
        <div className="border-t pt-4 space-y-3">
          <h4 className="text-sm font-semibold">Add New Policy</h4>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Policy Name (e.g. Privacy Policy)" value={newPolicy.policy_name} onChange={e => setNewPolicy({...newPolicy, policy_name: e.target.value})} />
            <Input placeholder="Slug (optional)" value={newPolicy.policy_slug} onChange={e => setNewPolicy({...newPolicy, policy_slug: e.target.value})} />
          </div>
          <FileUpload folder="policies" accept=".pdf" existingFiles={newPolicy.pdf_url ? [newPolicy.pdf_url] : []}
            onUpload={url => setNewPolicy({...newPolicy, pdf_url: url})} label="Upload PDF" />
          <Button data-testid="create-policy-btn" onClick={create} disabled={saving} className="rounded-full"><Plus className="h-3.5 w-3.5 mr-1" /> Add Policy</Button>
        </div>
      </CardContent>
    </Card>
  );
}
